import { useQuery } from '@tanstack/react-query';
import React, { FC, useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { InfoForm } from '../../Transfers/_components/Layout';
import { ROUTE } from '~/app/routes';
import { CopyInput, Details, Loading, PageHeader, RenderFields, TimelineEvents } from '~/components';
import { useNavigate } from '~/lib/navigation';
import { InterestPayout, InterestRepository } from '~/repositories';
import { BankAccountRepository } from '~/repositories/BankAccountRepository';
import { EntityRepository } from '~/repositories/EntityRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { formatDate } from '~/util';

import { InterestCalculationBreakdown } from './_components/InterestCalculationBreakdown';
import { InterestPayoutPageHeader } from './_components/InterestPayoutPageHeader';
import { buildInterestPayoutTimelineEvents } from './_utils/interestPayoutTimeline';

interface Params {
  id: string;
}

interface ExtendedInterestPayout extends InterestPayout {
  idempotencyKey?: string;
}

const formatData = (payout: ExtendedInterestPayout): Record<string, unknown> => {
  const formatted: Record<string, unknown> = {};
  Object.entries(payout).forEach(([key, value]) => {
    formatted[key] = value;
  });
  return formatted;
};

export const PageBankAccountsInterestPayoutView: FC = () => {
  const { id } = useParams<keyof Params>() as Params;
  const { isAuthorized } = useSessionStore();
  const navigate = useNavigate();
  const { addDangerNotification } = useNotificationStore();

  const {
    data: payout,
    isLoading: isPayoutLoading,
    error: payoutError,
  } = useQuery({
    queryKey: ['interest-payout', id],
    queryFn: async () => {
      if (!id) throw new Error('Payout ID is required');
      return InterestRepository.getPayout(id) as Promise<ExtendedInterestPayout>;
    },
    enabled: !!id,
    retry: false,
  });

  const {
    data: bankAccount,
    isLoading: isBankAccountLoading,
    error: bankAccountError,
  } = useQuery({
    queryKey: ['bank-account', payout?.productId],
    queryFn: async () => {
      if (!payout?.productId) throw new Error('Product ID is required');
      return BankAccountRepository.get({ id: payout.productId });
    },
    enabled: !!payout?.productId,
    retry: false,
  });

  const shouldFetchEntity =
    isAuthorized({ permission: 'entities', permissionLevel: 'read' }) && !!bankAccount?.owners?.[0];

  const { data: entity, error: entityError } = useQuery({
    queryKey: ['entity', bankAccount?.owners?.[0]],
    queryFn: async () => {
      if (!bankAccount?.owners?.[0]) throw new Error('Entity ID is required');
      return EntityRepository.get({ id: bankAccount.owners[0] });
    },
    enabled: shouldFetchEntity,
    retry: false,
  });

  const interestConfigId = (bankAccount as any)?.interestConfigId;
  const shouldFetchInterestConfig = !!interestConfigId && !!payout?.lastAccruedDate;

  const { data: interestConfig, error: interestConfigError } = useQuery({
    queryKey: ['interest-config', interestConfigId, payout?.lastAccruedDate],
    queryFn: async () => {
      if (!interestConfigId || !payout?.lastAccruedDate) {
        throw new Error('Interest config ID and last accrued date are required');
      }
      return InterestRepository.getConfig(interestConfigId, payout.lastAccruedDate);
    },
    enabled: shouldFetchInterestConfig,
    retry: false,
  });

  useEffect(() => {
    if (payoutError) {
      addDangerNotification({ content: (payoutError as Error).message });
      navigate(ROUTE.BANK_ACCOUNTS);
    }
  }, [payoutError, addDangerNotification, navigate]);

  useEffect(() => {
    if (bankAccountError) {
      addDangerNotification({ content: (bankAccountError as Error).message });
    }
  }, [bankAccountError, addDangerNotification]);

  useEffect(() => {
    if (entityError) {
      addDangerNotification({ content: (entityError as Error).message });
    }
  }, [entityError, addDangerNotification]);

  useEffect(() => {
    if (interestConfigError) {
      console.error('Failed to fetch interest config:', interestConfigError);
    }
  }, [interestConfigError]);

  const formattedPayout = useMemo(() => (payout ? formatData(payout) : undefined), [payout]);

  const formMethods = useForm<ExtendedInterestPayout>();

  useEffect(() => {
    if (payout) {
      formMethods.setValue('description', payout.description || '-');
      formMethods.setValue('type', payout.type || '-');
      formMethods.setValue('lastAccruedDate', payout.lastAccruedDate || '-');
      if ('idempotencyKey' in payout) {
        formMethods.setValue('idempotencyKey', (payout as any).idempotencyKey || '-');
      }
    }
  }, [payout, formMethods]);

  const isDataLoading = useMemo(() => {
    return isPayoutLoading || isBankAccountLoading;
  }, [isPayoutLoading, isBankAccountLoading]);

  const amountValue = useMemo(() => {
    if (!payout?.amount) return 0;
    return typeof payout.amount === 'string' ? parseFloat(payout.amount) : payout.amount;
  }, [payout]);

  return (
    <>
      <PageHeader text="Interest Payout" />

      <Loading isLoading={isDataLoading} fullHeight>
        {payout && (
          <InterestPayoutPageHeader
            amount={amountValue}
            payoutDate={payout.updatedAt || payout.createdAt}
            type={payout.type}
            payoutId={payout.id}
            currencyCode={payout.currencyCode}
          />
        )}
        {bankAccount && payout && (
          <TimelineEvents
            title={false}
            entries={buildInterestPayoutTimelineEvents({
              payout,
              bankAccount,
              entityType:
                entity?.type && entity.type !== 'TYPE_NONE'
                  ? (entity.type.toLowerCase() as 'person' | 'business')
                  : undefined,
              breakdownComponent: interestConfig ? (
                <InterestCalculationBreakdown
                  payoutAmount={amountValue}
                  currencyCode={payout.currencyCode}
                  lastAccruedDate={payout.lastAccruedDate}
                  interestConfig={interestConfig}
                  balance={bankAccount.balances?.availableAmount}
                />
              ) : undefined,
              navigate,
            })}
          />
        )}
        <FormProvider {...formMethods}>
          <InfoForm autoComplete="off">
            {RenderFields<ExtendedInterestPayout>({
              sections: [
                {
                  headline: 'Information',
                  fields: [
                    {
                      id: 'description',
                      label: 'Description',
                      children: ({ value }) => {
                        return <CopyInput value={String(value || '-')} />;
                      },
                    },
                    {
                      id: 'type',
                      label: 'Type',
                      children: ({ value }) => {
                        return <CopyInput value={String(value || '-')} />;
                      },
                    },
                    {
                      id: 'lastAccruedDate',
                      label: 'Last Accrued Date',
                      children: ({ value }) => {
                        if (!value) return <CopyInput value="-" />;
                        const dateValue = typeof value === 'string' ? value : String(value);
                        try {
                          const formattedDate = formatDate(dateValue, { dateFormat: 'short', timeZoneFormat: 'local' });
                          return <CopyInput value={formattedDate} />;
                        } catch {
                          return <CopyInput value={dateValue} />;
                        }
                      },
                    },
                  ],
                },
              ],
            })}
            <Details
              label="Interest Payout Details"
              formattedData={formattedPayout}
              rawData={payout as Record<string, unknown> | undefined}
            />
          </InfoForm>
        </FormProvider>
      </Loading>
    </>
  );
};
